import SpacingToken from "../api/model/spacings/SpacingToken";

type SizeUnit = "LONG" | "SHORT";

const NUMBER_SUFFIX_REGEX = /\d+$/;
const LAST_WORD_REGEX = /\w+$/;
const SIZE_UNITS: Record<SizeUnit, string[]> = {
    SHORT: ["xxs", "xs", "s", "m", "l", "xl", "xxl"],
    LONG: ["xxsmall", "xsmall", "small", "medium", "large", "xlarge", "xxlarge"]
};
const VALUE_INCREMENT_SIZE = 4;

/**
 * Generates the initial payload for spacing token.
 *  - For token value, if there is a normal sequence between values like 4px, 8px, 12px etc, then next value will be 16px.
 *    If there is fibonacci sequence like 4px, 8px, 12px, 20px etc, then next value will be 32px.
 *    Otherwise next value will be the nearest multiple of the 4px.
 *  - For token name, if the last token name ends with the one of the items of `SIZE_UNITS.SHORT` or `SIZE_UNITS.LONG`, then next token name will end with the next item of one of size units lists.
 *    If last token name ends with a number then new token name will end with the next available number.
 *    This number rule also applies if there is no available size unit to name the new token. For example, if the last token is `spacing-xxl` then the new token name will be `spacing-xxl-02`.
 * @param spacingTokens spacingTokens to be used to calculate new token's payload
 * @param spacingTokenNames spacingTokenNames represents the unique token name array
 * @param baseTokenValue baseTokenValue represents base spacing token's value
 */
// eslint-disable-next-line complexity
function generateInitialPayloadForSpacingToken(
    spacingTokens: SpacingToken[], spacingTokenNames: string[], baseTokenValue: number
): Omit<SpacingToken, "_id"> {
    // TODO: Token name length is limited to 100 chars on the API side. So there should be a check for that condition in this util
    const spacingTokensLength = spacingTokens.length;
    const lastToken = spacingTokens[spacingTokensLength - 1];
    const computedBaseToken = baseTokenValue || VALUE_INCREMENT_SIZE; // If the baseTokenValue is 0 then set it as VALUE_INCREMENT_SIZE
    const spacingToken = {
        token: "spacing-xxs",
        value: VALUE_INCREMENT_SIZE,
        color: {
            r: 153,
            g: 102,
            b: 204,
            a: 0.2
        }
    };

    if (spacingTokensLength) {
        const zeroValueTokenCount = spacingTokens[0].value ? 0 : 1;
        let sequentialPairCount = 0;
        let fibonacciPairCount = 0;
        let previousPairDiff: number | undefined;

        for (let index = 1; index < spacingTokensLength; index++) {
            const currentPairDiff = spacingTokens[index].value - spacingTokens[index - 1].value;

            if (sequentialPairCount === index - 1 && (!previousPairDiff || previousPairDiff === currentPairDiff)) {
                previousPairDiff = currentPairDiff;
                sequentialPairCount++;
            }

            if (index >= 2 && currentPairDiff === spacingTokens[index - 2].value) {
                fibonacciPairCount++;
            }
        }

        let newTokenValueIncrement;
        if (spacingTokensLength === 1) {
            newTokenValueIncrement = lastToken.value || VALUE_INCREMENT_SIZE;
        } else if (sequentialPairCount === spacingTokensLength - 1) {
            newTokenValueIncrement = previousPairDiff!;
        } else if (fibonacciPairCount === spacingTokensLength - 2 - zeroValueTokenCount) {
            newTokenValueIncrement = spacingTokens[spacingTokensLength - 2].value;
        } else {
            newTokenValueIncrement = computedBaseToken - (lastToken.value % computedBaseToken);
        }

        spacingToken.value = lastToken.value + newTokenValueIncrement;
    }

    if (spacingTokenNames.length) {
        const lastTokenName = lastToken ? lastToken.token : spacingToken.token;
        const lastTokenNameLastWordMatch = lastTokenName.match(LAST_WORD_REGEX);
        let isValidTokenNameFound = false;
        let validTokenName: string | undefined;

        if (lastTokenNameLastWordMatch) {
            const lastTokenNamePrefix = lastTokenName.slice(0, lastTokenNameLastWordMatch.index);
            const [lastTokenNameLastWord] = lastTokenNameLastWordMatch;
            let sizeUnitType: SizeUnit | undefined;

            if (SIZE_UNITS.SHORT.includes(lastTokenNameLastWord)) {
                sizeUnitType = "SHORT";
            } else if (SIZE_UNITS.LONG.includes(lastTokenNameLastWord)) {
                sizeUnitType = "LONG";
            }

            if (sizeUnitType) {
                let validSizeUnitIndex = SIZE_UNITS[sizeUnitType].indexOf(lastTokenNameLastWord);

                while (!isValidTokenNameFound && validSizeUnitIndex + 1 < SIZE_UNITS[sizeUnitType].length) {
                    validSizeUnitIndex++;
                    validTokenName = `${lastTokenNamePrefix}${SIZE_UNITS[sizeUnitType][validSizeUnitIndex]}`;
                    isValidTokenNameFound = !spacingTokenNames.includes(validTokenName);
                }
            }
        }

        if (!isValidTokenNameFound) {
            const lastTokenNameNumberSuffixMatch = lastTokenName.match(NUMBER_SUFFIX_REGEX);
            let lastTokenNamePrefix;
            let validNumberSuffix;

            if (lastTokenNameNumberSuffixMatch) {
                lastTokenNamePrefix = lastTokenName.slice(0, lastTokenNameNumberSuffixMatch.index);
                validNumberSuffix = parseInt(lastTokenNameNumberSuffixMatch[0], 10);
            } else {
                lastTokenNamePrefix = lastTokenName;
                validNumberSuffix = 1;
            }

            while (!isValidTokenNameFound) {
                validNumberSuffix++;
                validTokenName = lastTokenNamePrefix.endsWith("-") ? lastTokenNamePrefix : `${lastTokenNamePrefix}-`;
                validTokenName += validNumberSuffix;
                isValidTokenNameFound = !spacingTokenNames.includes(validTokenName);
            }
        }

        spacingToken.token = validTokenName!;
    }

    return spacingToken;
}

export { generateInitialPayloadForSpacingToken };
